import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiResponse } from '../types/api';
import { msalInstance, apiScopes } from '../auth/msalConfig';
import type { Environment } from '../contexts/EnvContext';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

async function getAccessToken(): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error('No authenticated user');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...apiScopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const response = await msalInstance.acquireTokenPopup(apiScopes);
      return response.accessToken;
    }
    throw err;
  }
}

function withEnv(params: Record<string, string> | undefined, env: Environment): Record<string, string> {
  return { ...params, env };
}

function getImpersonateHeaders(): Record<string, string> {
  const role = localStorage.getItem('impersonateRole');
  if (role && ['admin', 'marketing', 'user'].includes(role)) {
    return { 'X-Impersonate-Role': role };
  }
  return {};
}

async function fetchApi<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  const token = await getAccessToken();
  const response = await fetch(url.toString(), {
    headers: {
      'Authorization': `Bearer ${token}`,
      ...getImpersonateHeaders(),
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  const result: ApiResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'Unknown API error');
  }

  return result.data as T;
}

async function fetchApiMutate<T>(endpoint: string, method: string, body?: unknown): Promise<T> {
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);
  const token = await getAccessToken();

  const response = await fetch(url.toString(), {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      ...getImpersonateHeaders(),
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `API error: ${response.status}`);
  }

  const result: ApiResponse<T> = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Unknown API error');
  }
  return result.data as T;
}

export const api = {
  // Dashboard
  getDashboard: (env: Environment) =>
    fetchApi<import('../types/api').DashboardSummary>('/dashboard', withEnv(undefined, env)),

  // Queue
  getQueueStatus: (days: number, env: Environment) =>
    fetchApi<import('../types/api').QueueSummary>('/queue/status', withEnv({ days: String(days) }, env)),
  getErrorHistory: (hours: number, env: Environment) =>
    fetchApi<import('../types/api').ErrorHistoryEntry[]>('/queue/failed', withEnv({ hours: String(hours) }, env)),
  retryFailedByOrganisation: (organisationId: string, days: number, env: Environment) =>
    fetchApiMutate<{ retried: number }>(`/queue/retry/${organisationId}?days=${days}&env=${env}`, 'POST'),

  // Auto-retry canary — backend-scoped to prod, so no env parameter
  getAutoRetryStatus: () =>
    fetchApi<import('../types/api').AutoRetryStatus>('/queue/auto-retry'),
  saveAutoRetryConfig: (config: Partial<import('../types/api').AutoRetryConfig>) =>
    fetchApiMutate<import('../types/api').AutoRetryConfig>('/queue/auto-retry/config', 'PUT', config),
  runAutoRetryCycle: () =>
    fetchApiMutate<import('../types/api').AutoRetryCycleResult>('/queue/auto-retry/run', 'POST'),

  // Performance — windows are passed as minutes so 15 min / 1h / days share one param
  getStepPerformance: (minutes: number, env: Environment) =>
    fetchApi<import('../types/api').StepPerformance[]>('/performance/steps', withEnv({ minutes: String(minutes) }, env)),
  getOrganisationPerformance: (minutes: number, env: Environment) =>
    fetchApi<import('../types/api').OrganisationPerformance[]>('/performance/organisations', withEnv({ minutes: String(minutes) }, env)),

  // Delivery
  getWalletDelivery: (env: Environment) =>
    fetchApi<import('../types/api').WalletDeliveryByOrganisation[]>('/delivery/wallet', withEnv(undefined, env)),
  getEmailDelivery: (days: number, env: Environment) =>
    fetchApi<import('../types/api').EmailDeliverySummary>('/delivery/email', withEnv({ days: String(days) }, env)),

  // Signing
  getPdfSigningBatches: (hours: number, env: Environment) =>
    fetchApi<import('../types/api').SigningBatchInfo[]>('/signing/pdf', withEnv({ hours: String(hours) }, env)),
  getJsonLdSigningBatches: (hours: number, env: Environment) =>
    fetchApi<import('../types/api').SigningBatchInfo[]>('/signing/jsonld', withEnv({ hours: String(hours) }, env)),

  // Seal
  getSealConfigurations: (env: Environment) =>
    fetchApi<import('../types/api').SealConfigurationInfo[]>('/seal/configurations', withEnv(undefined, env)),
  getSealWarnings: (env: Environment) =>
    fetchApi<import('../types/api').SealWarning[]>('/seal/warnings', withEnv(undefined, env)),

  // Organisations
  getOrganisationList: (env: Environment) =>
    fetchApi<import('../types/api').OrganisationListItem[]>('/organisations/list', withEnv(undefined, env)),
  getOrganisationCredits: (env: Environment) =>
    fetchApi<import('../types/api').OrganisationCredits[]>('/organisations/credits', withEnv(undefined, env)),

  // Health
  getHealth: (env: Environment) =>
    fetchApi<import('../types/api').HealthCheckResult>('/health', withEnv(undefined, env)),

  // Users (global, not per-env)
  getCurrentUser: () =>
    fetchApi<import('../types/api').CurrentUser>('/users/me'),
  getUsers: () =>
    fetchApi<import('../types/api').AllowedUser[]>('/users'),
  addUser: (email: string, role: 'admin' | 'marketing' | 'user' = 'user') =>
    fetchApiMutate<{ email: string; role: string }>('/users', 'POST', { email, role }),
  removeUser: (email: string) =>
    fetchApiMutate<{ removed: string }>(`/users/${encodeURIComponent(email)}`, 'DELETE'),
  updateUserRole: (email: string, role: 'admin' | 'marketing' | 'user') =>
    fetchApiMutate<{ email: string; role: string }>(`/users/${encodeURIComponent(email)}`, 'PATCH', { role }),

  // Email Templates
  getEmailTemplates: (env: Environment) =>
    fetchApi<import('../types/api').EmailTemplateOverview[]>('/email-templates', withEnv(undefined, env)),
  getEmailTemplate: (database: string, templateName: string, language: string, env: Environment) =>
    fetchApi<import('../types/api').EmailTemplateDetail>(`/email-templates/${database}/${templateName}/${language}`, withEnv(undefined, env)),
  saveEmailTemplate: (database: string, templateName: string, language: string, data: { subject: string; body: string; fromDisplayName?: string; replyTo?: string }, env: Environment) =>
    fetchApiMutate<import('../types/api').EmailTemplateDetail>(`/email-templates/${database}/${templateName}/${language}?env=${env}`, 'PUT', data),
  deleteEmailTemplate: (database: string, templateName: string, language: string, env: Environment) =>
    fetchApiMutate<void>(`/email-templates/${database}/${templateName}/${language}?env=${env}`, 'DELETE'),
  getPlaceholderInfo: (templateName: string) =>
    fetchApi<import('../types/api').PlaceholderInfo>(`/email-templates/placeholders/${templateName}`),

  // Credential Templates
  getCredentialTemplates: (archived: boolean, env: Environment) =>
    fetchApi<import('../types/api').CredentialTemplateOverview[]>('/credential-templates', withEnv({ archived: String(archived) }, env)),
  archiveCredentialTemplate: (id: string, env: Environment) =>
    fetchApiMutate<{ archived: boolean }>(`/credential-templates/${id}/archive?env=${env}`, 'POST'),
  unarchiveCredentialTemplate: (id: string, env: Environment) =>
    fetchApiMutate<{ unarchived: boolean }>(`/credential-templates/${id}/unarchive?env=${env}`, 'POST'),

  // Certificate Templates
  getCertificateDesigns: (env: Environment) =>
    fetchApi<import('../types/api').DiplomaDesignOverview[]>('/certificate-templates/designs', withEnv(undefined, env)),
  getCertificateDesign: (slug: string, env: Environment) =>
    fetchApi<import('../types/api').DiplomaDesignOverview>(`/certificate-templates/designs/${slug}`, withEnv(undefined, env)),
  createCertificateDesign: (data: { name: string; slug: string; type: string; organisationId?: string; sortOrder?: number }, env: Environment) =>
    fetchApiMutate<import('../types/api').DiplomaDesignOverview>(`/certificate-templates/designs?env=${env}`, 'POST', data),
  updateCertificateDesign: (slug: string, data: { name?: string; isActive?: boolean; sortOrder?: number; type?: string; organisationId?: string | null }, env: Environment) =>
    fetchApiMutate<import('../types/api').DiplomaDesignOverview>(`/certificate-templates/designs/${slug}?env=${env}`, 'PATCH', data),
  deleteCertificateDesign: (slug: string, env: Environment) =>
    fetchApiMutate<{ deleted: boolean }>(`/certificate-templates/designs/${slug}?env=${env}`, 'DELETE'),
  createVariantUpload: (slug: string, lang: string, env: Environment) =>
    fetchApiMutate<import('../types/api').SasUploadResponse>(`/certificate-templates/designs/${slug}/variants/${lang}?env=${env}`, 'POST', {}),
  deleteVariant: (slug: string, lang: string, env: Environment) =>
    fetchApiMutate<{ deleted: boolean }>(`/certificate-templates/designs/${slug}/variants/${lang}?env=${env}`, 'DELETE'),
  bulkUploadVariants: async (slug: string, file: File, env: Environment): Promise<import('../types/api').BulkUploadResponse> => {
    const token = await getAccessToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_BASE}/certificate-templates/designs/${slug}/variants/bulk?env=${env}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        ...getImpersonateHeaders(),
        // Note: Do NOT set Content-Type — browser sets it with boundary for multipart
      },
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `API error: ${response.status}`);
    }

    const result: import('../types/api').ApiResponse<import('../types/api').BulkUploadResponse> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Unknown API error');
    }
    return result.data as import('../types/api').BulkUploadResponse;
  },
  createDesignFontUpload: (slug: string, fontName: string, env: Environment) =>
    fetchApiMutate<import('../types/api').SasUploadResponse>(`/certificate-templates/designs/${slug}/fonts?env=${env}`, 'POST', { fontName }),
  deleteDesignFont: (slug: string, fontId: string, env: Environment) =>
    fetchApiMutate<{ deleted: boolean }>(`/certificate-templates/designs/${slug}/fonts/${fontId}?env=${env}`, 'DELETE'),
  renameDesignFont: (slug: string, fontId: string, fontName: string, env: Environment) =>
    fetchApiMutate<{ renamed: boolean }>(`/certificate-templates/designs/${slug}/fonts/${fontId}?env=${env}`, 'PATCH', { fontName }),
  createThumbnailUpload: (slug: string, env: Environment) =>
    fetchApiMutate<import('../types/api').SasUploadResponse>(`/certificate-templates/designs/${slug}/thumbnail?env=${env}`, 'POST', {}),
  getGlobalFonts: (env: Environment) =>
    fetchApi<import('../types/api').GlobalFontInfo[]>('/certificate-templates/fonts', withEnv(undefined, env)),
  createGlobalFontUpload: (fontName: string, env: Environment) =>
    fetchApiMutate<import('../types/api').SasUploadResponse>(`/certificate-templates/fonts?env=${env}`, 'POST', { fontName }),
  deleteGlobalFont: (fontName: string, env: Environment) =>
    fetchApiMutate<{ deleted: boolean }>(`/certificate-templates/fonts/${encodeURIComponent(fontName)}?env=${env}`, 'DELETE'),
  invalidateFontCache: (env: Environment) =>
    fetchApiMutate<import('../types/api').CacheInvalidationResponse>(`/certificate-templates/cache/invalidate?env=${env}`, 'POST', {}),

  // PDF Preview
  createPreviewImageUpload: (imageType: 'issuer' | 'badge', env: Environment) =>
    fetchApiMutate<import('../types/api').SasUploadResponse>(
      `/certificate-templates/preview-images/${imageType}?env=${env}`,
      'POST',
      {},
    ),
  previewPdf: (slug: string, variantId: string, body: import('../types/api').PreviewPdfRequest, env: Environment) =>
    fetchApiMutate<import('../types/api').PreviewPdfResponse>(
      `/certificate-templates/designs/${slug}/variants/${variantId}/preview-pdf?env=${env}`,
      'POST',
      body,
    ),

  // Alerting (global, not per-env)
  getAlertRecipients: () =>
    fetchApi<import('../types/api').AlertRecipient[]>('/alerting/recipients'),
  addAlertRecipient: (data: { email: string; name: string; groups: string }) =>
    fetchApiMutate<{ email: string }>('/alerting/recipients', 'POST', data),
  updateAlertRecipient: (email: string, data: { name?: string; groups?: string; isActive?: boolean }) =>
    fetchApiMutate<any>(`/alerting/recipients/${encodeURIComponent(email)}`, 'PATCH', data),
  removeAlertRecipient: (email: string) =>
    fetchApiMutate<{ removed: string }>(`/alerting/recipients/${encodeURIComponent(email)}`, 'DELETE'),

  getAlertRules: () =>
    fetchApi<import('../types/api').AlertRule[]>('/alerting/rules'),
  upsertAlertRule: (rule: import('../types/api').AlertRule) =>
    fetchApiMutate<{ ruleId: string }>('/alerting/rules', 'POST', rule),
  updateAlertRule: (ruleId: string, data: Partial<import('../types/api').AlertRule>) =>
    fetchApiMutate<any>(`/alerting/rules/${encodeURIComponent(ruleId)}`, 'PATCH', data),
  deleteAlertRule: (ruleId: string) =>
    fetchApiMutate<{ deleted: string }>(`/alerting/rules/${encodeURIComponent(ruleId)}`, 'DELETE'),

  getSmtpConfig: () =>
    fetchApi<import('../types/api').SmtpConfigResponse>('/alerting/smtp'),
  saveSmtpConfig: (data: any) =>
    fetchApiMutate<{ saved: boolean }>('/alerting/smtp', 'PUT', data),
  testSmtp: () =>
    fetchApiMutate<{ sent: boolean; to: string }>('/alerting/smtp/test', 'POST'),

  getAlertHistory: (page: number = 1, status: import('../types/api').AckStatus | 'all' = 'all', pageSize: number = 50) =>
    fetchApi<import('../types/api').AlertHistoryResponse>('/alerting/history', { page: String(page), pageSize: String(pageSize), status }),
  updateAlertHistoryStatus: (id: string, status: 'acknowledged' | 'cleared') =>
    fetchApiMutate<{ id: string; ackStatus: import('../types/api').AckStatus }>(`/alerting/history/${encodeURIComponent(id)}/status`, 'PATCH', { status }),
  runAlertCheck: () =>
    fetchApiMutate<import('../types/api').AlertCheckResult[]>('/alerting/check', 'POST'),

  // Issuance Test (per-env)
  getIssuanceTestConfig: (env: Environment) =>
    fetchApi<import('../types/api').IssuanceTestConfig>('/issuance-test/config', withEnv(undefined, env)),
  saveIssuanceTestConfig: (data: { apiKey?: string; recipientDomain?: string }, env: Environment) =>
    fetchApiMutate<import('../types/api').IssuanceTestConfig>(`/issuance-test/config?env=${env}`, 'PUT', data),
  getIssuanceTestTemplates: (env: Environment) =>
    fetchApi<import('../types/api').IssuanceTestTemplate[]>('/issuance-test/templates', withEnv(undefined, env)),
  startIssuanceTestRun: (data: { templateId: string; count: number; grade?: string }, env: Environment) =>
    fetchApiMutate<import('../types/api').IssuanceTestRunSummary>(`/issuance-test/runs?env=${env}`, 'POST', data),
  getIssuanceTestRuns: (env: Environment) =>
    fetchApi<import('../types/api').IssuanceTestRunSummary[]>('/issuance-test/runs', withEnv(undefined, env)),
  getIssuanceTestRunResult: (runId: string, env: Environment) =>
    fetchApi<import('../types/api').IssuanceTestRunDetail>(`/issuance-test/runs/${runId}`, withEnv(undefined, env)),
};
